import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MessageContentProps {
    content: string;
    role: 'user' | 'assistant' | 'system';
}

/**
 * Simple content cleaner - just removes image markdown lines
 * No complex reorganization that could interfere with streaming
 */
const cleanContent = (content: string): string => {
    // Remove all image markdown lines (![alt](url))
    return content.split('\n')
        .filter(line => !line.trim().match(/^!\[.*\]\(.*\)$/))
        .join('\n');
};

/**
 * Safely extract text from React children
 * Handles strings, numbers, arrays, and nested React elements
 */
const getTextFromChildren = (children: React.ReactNode): string => {
    if (children === null || children === undefined) {
        return '';
    }
    if (typeof children === 'string') {
        return children;
    }
    if (typeof children === 'number') {
        return String(children);
    }
    if (Array.isArray(children)) {
        return children.map(getTextFromChildren).join('');
    }
    if (React.isValidElement(children)) {
        // For React elements, try to get text from props.children
        const props = children.props as { children?: React.ReactNode };
        if (props && props.children) {
            return getTextFromChildren(props.children);
        }
        return '';
    }
    // Fallback - try to convert but avoid [object Object]
    const str = String(children);
    if (str === '[object Object]') {
        return '';
    }
    return str;
};

export const MessageContent: React.FC<MessageContentProps> = ({ content, role }) => {
    // Simple content cleaning - just removes images, no complex reorganization
    const cleanedContent = useMemo(() => {
        return cleanContent(content);
    }, [content]);

    // Custom components for rendering markdown elements
    const components = {
        // Simple link rendering - let the browser handle it naturally
        a: ({ href, children }: any) => {
            const linkText = getTextFromChildren(children);
            const isSeeProduct = linkText.includes('See Product') || linkText.includes('View Details') || linkText.includes('View Listing');

            if (isSeeProduct) {
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-2 px-2 py-1 text-xs bg-cyan-400 hover:bg-cyan-100 text-black hover:text-black font-medium rounded-full border border-cyan-200 no-underline transition-all duration-200">
                        {children}
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>
                );
            }

            return (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 no-underline hover:underline decoration-1 underline-offset-2 font-medium transition-colors duration-200">
                    {children}
                </a>
            );
        },

        hr: () => (
            <hr className="my-4 border-t border-gray-600/50" />
        ),

        // Remove image rendering - backend now generates text links instead
        img: ({ src, alt }: any) => {
            return null; // Don't render images at all
        },

        // Style headers with special formatting for sections - mobile responsive
        h2: ({ children }: any) => {
            const text = getTextFromChildren(children);
            const isVerifiedSection = text.includes('Verified Results') ||
                text.includes('Verified Partners') ||
                text.includes('verified results');
            const isOnlineSection = text.includes('Online Listings') ||
                text.includes('Additional Options') ||
                text.includes('online listings');

            // Section dividers with horizontal lines
            if (isVerifiedSection || isOnlineSection) {
                return (
                    <div className="mt-4 sm:mt-6 mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-xl sm:text-2xl">{isVerifiedSection ? '🚗' : '🔍'}</span>
                            <h2 className="text-base sm:text-lg font-bold text-gray-800">
                                {isVerifiedSection ? 'Verified Results' : 'Online Listings'}
                            </h2>
                        </div>
                        <div className="mt-2 border-b-2 border-gray-300"></div>
                    </div>
                );
            }

            return (
                <h2 className="text-lg sm:text-xl font-bold mt-3 sm:mt-4 mb-2 text-gray-900">
                    {children}
                </h2>
            );
        },

        h3: ({ children }: any) => {
            const text = getTextFromChildren(children);
            const hasVerifiedCheck = text.includes('✓');

            // Remove the ✓ from the text since we'll show it in the badge
            const cleanedText = hasVerifiedCheck ? text.replace('✓', '').trim() : text;

            return (
                <h3 className="text-base sm:text-lg font-semibold mt-4 mb-3 text-gray-900 flex items-center gap-2">
                    <span>{cleanedText}</span>
                    {hasVerifiedCheck && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 text-cyan-800 text-xs font-medium rounded-full border border-cyan-200">
                    <span>✓</span>
                    <span>Verified</span>
                </span>
                    )}
                </h3>
            );
        },


        // Style lists - mobile responsive
        ul: ({ children }: any) => (
            <ul className="list-disc ml-4 sm:ml-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base">{children}</ul>
        ),

        // Style paragraphs - mobile responsive
        p: ({ children }: any) => (
            <p className="mb-2 leading-relaxed text-sm sm:text-base">{children}</p>
        ),

        // Style emphasis - mobile responsive badges
        em: ({ children }: any) => {
            const text = getTextFromChildren(children);
            if (text.includes('Sponsored') || text.includes('Featured')) {
                return (
                    <span className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-800 text-[10px] sm:text-xs font-medium rounded-full border border-cyan-200">
                        <span className="text-cyan-600">✓</span>
                        <span>Verified</span>
                    </span>
                );
            }
            return <em className="italic">{children}</em>;
        },

        // Style strong/bold text - mobile responsive
        strong: ({ children }: any) => (
            <strong className="font-bold text-gray-900 text-sm sm:text-base">{children}</strong>
        ),

        // Style code blocks - mobile responsive
        code: ({ inline, className, children }: any) => {
            if (inline) {
                return <code className="bg-gray-100 px-1 py-0.5 rounded text-xs sm:text-sm">{children}</code>;
            }
            return (
                <pre className="bg-gray-100 p-2 sm:p-3 rounded-lg overflow-x-auto text-xs sm:text-sm">
                    <code className={className}>{children}</code>
                </pre>
            );
        },

        // Style tables for vehicle comparisons - IMPROVED for both mobile and desktop
        table: ({ children }: any) => (
            <div className="my-6 w-full">
                {/* Desktop/Tablet: Constrained width with visible scrollbar */}
                <div className="hidden sm:block">
                    <div
                        className="overflow-x-auto"
                        style={{
                            maxWidth: '600px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#06b6d4 #374151'
                        }}
                    >
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden rounded-xl border border-gray-600 shadow-lg">
                                <table className="min-w-full divide-y divide-gray-600" style={{ tableLayout: 'auto' }}>
                                    {children}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile: Responsive table with horizontal scroll only when needed */}
                <div className="block sm:hidden">
                    <div
                        className="overflow-x-auto -mx-4 px-4 pb-2"
                        style={{
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#06b6d4 #1f2937'
                        }}
                    >
                        <div className="min-w-full">
                            <div className="overflow-hidden rounded-xl border border-gray-600 shadow-lg">
                                <table className="min-w-full divide-y divide-gray-600" style={{ tableLayout: 'auto' }}>
                                    {children}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ),

        // Style table headers - properly responsive
        thead: ({ children }: any) => (
            <thead className="bg-gray-700">
            {children}
            </thead>
        ),

        // Style table body - properly responsive
        tbody: ({ children }: any) => (
            <tbody className="bg-gray-800 divide-y divide-gray-600">
            {children}
            </tbody>
        ),

        // Style table header cells - properly responsive
        th: ({ children }: any) => (
            <th className="px-1.5 py-1.5 sm:px-4 sm:py-3 md:px-6 md:py-4 text-left text-[9px] sm:text-xs md:text-sm font-semibold text-gray-200 uppercase tracking-wider whitespace-nowrap">
                {children}
            </th>
        ),

        // Style table data cells - properly responsive
        td: ({ children }: any) => {
            const text = getTextFromChildren(children);

            // Handle star ratings
            if (text.includes('⭐')) {
                return (
                    <td className="px-1.5 py-1.5 sm:px-4 sm:py-3 md:px-6 md:py-4 text-[10px] sm:text-sm md:text-base whitespace-nowrap">
                        <span className="text-yellow-400">{text}</span>
                    </td>
                );
            }

            // Handle price ranges
            if (text.includes('$')) {
                return (
                    <td className="px-1.5 py-1.5 sm:px-4 sm:py-3 md:px-6 md:py-4 text-[9px] sm:text-sm md:text-base whitespace-nowrap">
                        <span className="font-semibold text-white">{children}</span>
                    </td>
                );
            }

            // Handle Yes/No badges
            const isYes = text.trim().toLowerCase() === 'yes';
            const isNo = text.trim().toLowerCase() === 'no';

            return (
                <td className="px-1.5 py-1.5 sm:px-4 sm:py-3 md:px-6 md:py-4 text-[9px] sm:text-sm md:text-base text-gray-300 break-words">
                    {isYes && (
                        <span className="inline-flex items-center gap-0.5 sm:gap-1.5 px-1.5 py-0.5 sm:px-3 sm:py-1 bg-green-600 text-white text-[9px] sm:text-xs md:text-sm font-semibold rounded-full whitespace-nowrap">
                            <svg className="w-2.5 h-2.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            <span className="hidden xs:inline">Yes</span>
                            <span className="inline xs:hidden">Y</span>
                        </span>
                    )}
                    {isNo && (
                        <span className="inline-flex items-center gap-0.5 sm:gap-1.5 px-1.5 py-0.5 sm:px-3 sm:py-1 bg-gray-600 text-gray-200 text-[9px] sm:text-xs md:text-sm font-semibold rounded-full whitespace-nowrap">
                            <svg className="w-2.5 h-2.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                            </svg>
                            <span className="hidden xs:inline">No</span>
                            <span className="inline xs:hidden">N</span>
                        </span>
                    )}
                    {!isYes && !isNo && children}
                </td>
            );
        },

        blockquote: ({ children }: any) => {
            const text = getTextFromChildren(children);

            // Detect callout type
            let bgColor = 'bg-cyan-500/80';
            let borderColor = 'border-cyan-300';
            let textColor = 'text-black';
            let icon = '💬';

            if (text.includes('⚠️') || text.includes('Important')) {
                bgColor = 'bg-yellow-900/40';
                borderColor = 'border-yellow-400';
                textColor = 'text-gray-200';
                icon = '⚠️';
            } else if (text.includes('💡') || text.includes('Tip')) {
                bgColor = 'bg-green-900/40';
                borderColor = 'border-green-400';
                textColor = 'text-gray-200';
                icon = '💡';
            } else if (text.includes('✅') || text.includes('Recommended')) {
                bgColor = 'bg-emerald-900/40';
                borderColor = 'border-emerald-400';
                textColor = 'text-gray-200';
                icon = '✅';
            } else if (text.includes('Would you like')) {
                // CTA style
                bgColor = 'bg-cyan-900/40';
                borderColor = 'border-cyan-400';
                textColor = 'text-gray-200';
                icon = '🔍';
            }

            return (
                <blockquote className={`my-4 pl-4 border-l-4 ${borderColor} ${bgColor} py-3 pr-4 rounded-r-lg flex gap-3`}>
                    <span className="text-xl flex-shrink-0">{icon}</span>
                    <div className={`text-sm sm:text-base font-medium ${textColor}`}>
                        {children}
                    </div>
                </blockquote>
            );
        },

        h4: ({ children }: any) => {
            const text = getTextFromChildren(children);
            let icon = '';

            if (text.includes('✅') || text.includes('Strengths') || text.includes('Pros')) {
                icon = '✅ ';
            } else if (text.includes('⚠️') || text.includes('Considerations') || text.includes('Cons')) {
                icon = '⚠️ ';
            } else if (text.includes('Key Features')) {
                icon = '🔑 ';
            }

            return (
                <h4 className="text-base sm:text-lg font-bold mt-4 mb-2 text-gray-200 flex items-center gap-2">
                    {icon && <span>{icon}</span>}
                    <span>{children}</span>
                </h4>
            );
        },
        // Style badge-style list items
        li: ({ children }: any) => {
            // Check if first text starts with emoji
            const childArray = React.Children.toArray(children);
            const firstText = childArray.find(child => typeof child === 'string');
            const startsWithCheck = firstText && typeof firstText === 'string' ? firstText.trim() : '';

            // Helper to remove emoji from first text node only
            const removeEmojiFromFirst = (emoji: string) => {
                return React.Children.map(children, (child, index) => {
                    if (index === 0 && typeof child === 'string') {
                        return child.replace(emoji, '').trim();
                    }
                    return child;
                });
            };

            // Style based on emoji prefix
            if (startsWithCheck.startsWith('✅')) {
                return (
                    <li className="flex items-start gap-2 mb-2">
                        <span className="text-green-400 flex-shrink-0 mt-0.5">✅</span>
                        <span className="text-gray-300">{removeEmojiFromFirst('✅')}</span>
                    </li>
                );
            } else if (startsWithCheck.startsWith('⚠️')) {
                return (
                    <li className="flex items-start gap-2 mb-2">
                        <span className="text-yellow-400 flex-shrink-0 mt-0.5">⚠️</span>
                        <span className="text-gray-300">{removeEmojiFromFirst('⚠️')}</span>
                    </li>
                );
            } else if (startsWithCheck.startsWith('❌')) {
                return (
                    <li className="flex items-start gap-2 mb-2">
                        <span className="text-red-400 flex-shrink-0 mt-0.5">❌</span>
                        <span className="text-gray-300">{removeEmojiFromFirst('❌')}</span>
                    </li>
                );
            }

            // Default list item
            return <li className="mb-2 text-gray-300">{children}</li>;
        },
    };

    return (
        <div className={`message-content ${role === 'user' ? 'text-white' : 'text-gray-900'}`}>
            <ReactMarkdown
                key={content.length}
                remarkPlugins={[remarkGfm]}
                components={components}
            >
                {cleanedContent}
            </ReactMarkdown>
        </div>
    );
};